/**
 * Data-layer helpers that write structured data into window.digitalData.
 *
 * Adobe Launch reads this object before every event fires.
 * Always call setPageInfo() BEFORE firing any tracking event.
 */

/** Creates the digitalData skeleton if it does not already exist. */
export const initializeDigitalData = (): void => {
  globalThis.window.digitalData ??= {};
  globalThis.window.digitalData.page ??= {};
  globalThis.window.digitalData.user ??= {};
  globalThis.window.digitalData.site ??= {};
  globalThis.window.digitalData.event ??= {};
};

/**
 * Writes page metadata into window.digitalData.page.pageInfo.
 * Also persists the current page name in sessionStorage for back-navigation tracking.
 *
 * @param pageName - Fully-qualified KP page name (e.g. "kporg:en:nat:mykp:ubt:home")
 * @param pageData - Optional extra page metadata
 */
export const setPageInfo = (
  pageName: string,
  pageData?: {
    pageTemplate?: string;
    pageType?: string;
    contentTitle?: string;
    contentType?: string;
  }
): void => {
  if (!globalThis.window.digitalData) initializeDigitalData();

  globalThis.window.digitalData!.page = {
    ...globalThis.window.digitalData!.page,
    pageInfo: {
      page: pageName,
      pageNameGranular: pageName,
      pageName: pageName.split(':').pop() ?? pageName,
      pageTemplate: pageData?.pageTemplate ?? '',
      pageType: pageData?.pageType ?? '',
      previousPage: sessionStorage.getItem('ubt_previousPage') ?? '',
      previousLink: document.referrer,
    },
  };

  // Use sessionStorage to scope previous-page tracking per tab
  sessionStorage.setItem('ubt_previousPage', pageName);
};

/**
 * Writes user / employee information into window.digitalData.user.
 * Call once after authentication resolves.
 *
 * @param userData - Authenticated user attributes
 */
export const setUserInfo = (userData: {
  nuid?: string;
  email?: string;
  region?: string;
  loginState?: 'Authenticated' | 'Unauthenticated';
  jobRole?: string;
  union?: string;
  workforceClass?: string;
  employeeTenure?: string;
}): void => {
  if (!globalThis.window.digitalData) initializeDigitalData();

  globalThis.window.digitalData!.user = {
    userID: userData.nuid ?? '',
    loginState: userData.loginState ?? 'Unauthenticated',
    profile: { region: userData.region ?? '' },
    job: { jobRole: userData.jobRole ?? '' },
    employee: {
      union: userData.union ?? '',
      workforceClass: userData.workforceClass ?? '',
      employeeTenure: userData.employeeTenure ?? '',
    },
    timestamp: new Date().toISOString(),
    userKnown: !!userData.nuid,
  };
};

/**
 * Writes site-level metadata into window.digitalData.site.
 * Call once during analytics initialization.
 *
 * @param appName - Short application identifier (e.g. "ubt")
 */
export const setSiteInfo = (appName: string): void => {
  if (!globalThis.window.digitalData) initializeDigitalData();

  globalThis.window.digitalData!.site = {
    siteName: 'kporg',
    domain: 'mykp',
    subdomain: 'ubt',
    language: 'en',
    charSet: 'UTF-8',
    applicationName: appName,
  };
};

/**
 * Writes content metadata into window.digitalData.page.content.
 * Useful for CMS-driven or form-step pages.
 *
 * @param contentData - Content attributes
 */
export const setContentInfo = (contentData: {
  title?: string;
  type?: string;
}): void => {
  if (!globalThis.window.digitalData) initializeDigitalData();

  globalThis.window.digitalData!.page = {
    ...globalThis.window.digitalData!.page,
    content: {
      title: contentData.title ?? '',
      type: contentData.type ?? 'page',
    },
  };
};

/** Logs the current digitalData state to the console for debugging. */
export const logDigitalData = (): void => {
  console.table(globalThis.window.digitalData);
};
